import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import VerificationsList from '@/components/admin/pendencias/VerificationsList';
import TicketsList from '@/components/admin/pendencias/TicketsList';
import { FileCheck, MessageSquare, Clock } from 'lucide-react';

const AdminPendencias = () => {
  const [filterType, setFilterType] = useState<'all' | 'waiting' | 'pending' | 'tickets'>('all');

  // Fetch verifications waiting
  const { data: waitingVerifications, isLoading: loadingWaiting, refetch: refetchWaiting } = useQuery({
    queryKey: ['admin-verifications', 'waiting'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_admin_verification_details', { p_status: 'waiting' });
      if (error) throw error;
      return data;
    },
  });

  // Fetch verifications pending
  const { data: pendingVerifications, isLoading: loadingPending } = useQuery({
    queryKey: ['admin-verifications', 'pending'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_admin_verification_details', { p_status: 'pending' });
      if (error) throw error;
      return data;
    },
  });

  // Fetch tickets
  const { data: tickets, isLoading: loadingTickets, refetch: refetchTickets } = useQuery({
    queryKey: ['admin-tickets'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_admin_tickets_details', { 
        p_status: ['open', 'in_progress'] 
      });
      if (error) throw error;
      return data;
    },
  });

  const totalCount = (waitingVerifications?.length || 0) + 
                   (pendingVerifications?.length || 0) + 
                   (tickets?.length || 0);

  return (
    <div className="px-4 pt-8 space-y-6 pb-20">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Atividades Pendentes</h2>
        <p className="text-white/70">Gerencie verificações e tickets de suporte</p>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Filtrar por tipo</h3>
        <Select value={filterType} onValueChange={(value) => setFilterType(value as any)}>
          <SelectTrigger className="w-[280px] bg-black/50 border-white/10 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-black border-white/10">
            <SelectItem value="all" className="text-white">
              Todos ({totalCount})
            </SelectItem>
            <SelectItem value="waiting" className="text-yellow-400">
              <FileCheck className="h-4 w-4 mr-2 inline" />
              Aguardando Revisão ({waitingVerifications?.length || 0})
            </SelectItem>
            <SelectItem value="pending" className="text-blue-400">
              <Clock className="h-4 w-4 mr-2 inline" />
              Em Andamento ({pendingVerifications?.length || 0})
            </SelectItem>
            <SelectItem value="tickets" className="text-luxury-red">
              <MessageSquare className="h-4 w-4 mr-2 inline" />
              Tickets ({tickets?.length || 0})
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(filterType === 'all' || filterType === 'waiting') && waitingVerifications && waitingVerifications.length > 0 && (
        <div className="mb-8">
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
            <FileCheck className="h-5 w-5 text-yellow-400" />
            Aguardando Revisão
          </h4>
          <VerificationsList 
            verifications={waitingVerifications} 
            isLoading={loadingWaiting}
            onActionComplete={refetchWaiting}
            type="waiting"
          />
        </div>
      )}

      {(filterType === 'all' || filterType === 'pending') && pendingVerifications && pendingVerifications.length > 0 && (
        <div className="mb-8">
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-400" />
            Em Andamento
          </h4>
          <VerificationsList 
            verifications={pendingVerifications} 
            isLoading={loadingPending}
            type="pending"
          />
        </div>
      )}

      {(filterType === 'all' || filterType === 'tickets') && tickets && tickets.length > 0 && (
        <div className="mb-8">
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-luxury-red" />
            Tickets de Suporte
          </h4>
          <TicketsList 
            tickets={tickets} 
            isLoading={loadingTickets}
            onActionComplete={refetchTickets}
          />
        </div>
      )}
    </div>
  );
};

export default AdminPendencias;
